import { useQuery } from '@tanstack/react-query';
import { userApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
// Deep import on purpose: the package barrel (`dist/index.js`) is a CommonJS `__exportStar` re-export
// whose names Rollup cannot see statically, so `import { resolveRole } from '@cds/shared'` fails the
// production build ("is not exported by packages/shared/dist/index.js"). The leaf module assigns
// `exports.AppRole = …` directly and bundles cleanly. Same source file the API enforces with.
import { AppRole as SharedRole, ROLE_LEVELS, resolveRole } from '@cds/shared/dist/models/roles.js';
import type { AppRole as SharedAppRole } from '@cds/shared/dist/models/roles.js';

/**
 * Role names as the UI spells them. The enum and the ordering live in `@cds/shared`
 * (`packages/shared/src/models/roles.ts`) — the same source the API enforces with; this file used to
 * carry its own copy of both, which is how a role could mean one thing to the server and another to
 * the menu. String literals stay accepted so call sites read as before (`hasRole('creator')`).
 */
export type AppRole = `${SharedAppRole}`;

export function useRole() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const account = useAuthStore((s) => s.account);
  const role = useAuthStore((s) => s.role);
  const setRole = useAuthStore((s) => s.setRole);

  useQuery({
    queryKey: ['user-profile-role'],
    queryFn: async () => {
      const profile = await userApi.getProfile();
      const r = resolveRole(profile.role) as AppRole;
      setRole(r);
      return r;
    },
    enabled: isAuthenticated && !role,
    staleTime: 5 * 60 * 1000, // 5 min
    retry: 1,
  });

  const currentRole: AppRole = resolveRole(role) as AppRole;

  // The signed-in address, and the Entra object ID behind it. Both are needed: a record written
  // through the hub holds an email in `createdBy`, one written by SkillBuilder holds the oid, and
  // the same person is behind both. Matching on email alone hid the Edit button from authors on
  // their own skills — the same mismatch the API used to refuse the write for.
  const email = account?.username?.toLowerCase() ?? null;
  const oid = account?.localAccountId?.toLowerCase() ?? null;

  return {
    role: currentRole,
    email,
    /**
     * Owner test. Takes the record when there is one, so both stored identities are considered;
     * a bare string still works for records that only ever carry the one field.
     */
    isAuthor: (subject?: string | null | { createdBy?: string; createdByOid?: string }) => {
      const claims = new Set([email, oid].filter(Boolean) as string[]);
      if (!claims.size || !subject) return false;
      const held = typeof subject === 'string'
        ? [subject]
        : [subject.createdBy, subject.createdByOid];
      return held.some((h) => !!h && claims.has(h.toLowerCase()));
    },
    hasRole: (required: AppRole) => ROLE_LEVELS[currentRole as SharedAppRole] >= ROLE_LEVELS[required as SharedAppRole],
    isCreator: ROLE_LEVELS[currentRole as SharedAppRole] >= ROLE_LEVELS[SharedRole.Creator],
    isPowerUser: ROLE_LEVELS[currentRole as SharedAppRole] >= ROLE_LEVELS[SharedRole.PowerUser],
    isMember: true, // everyone is at least a member
  };
}
